import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { PROFILE } from "../../data/portfolio";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Work", href: "#work" },
  { label: "Skills", href: "#skills" },
  { label: "Connect", href: "#connect" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border] duration-500 ${
        scrolled ? "glass border-b border-[#1F2D3D]/10" : "border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 h-20">
        <button
          onClick={() => go("#top")}
          className="font-display text-lg font-semibold tracking-tight hover:text-[#5E7E9B] transition-colors duration-300"
          data-testid="nav-logo"
        >
          {PROFILE.firstName}
          <span className="text-[#5E7E9B]">.</span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="group relative font-mono text-xs uppercase tracking-[0.15em] text-[#5B6472] hover:text-[#1F2D3D] transition-colors duration-300"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#5E7E9B] group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}
        </div>

        <button
          onClick={() => go("#connect")}
          className="hidden md:inline-flex items-center px-5 py-2 rounded-full border border-[#5E7E9B]/40 text-[#5E7E9B] font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#5E7E9B] hover:text-white transition-colors duration-300"
          data-testid="nav-hire-btn"
        >
          Let's Talk
        </button>

        <button
          className="md:hidden text-[#1F2D3D]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden glass border-t border-[#1F2D3D]/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl text-[#404B5A] hover:text-[#5E7E9B] transition-colors"
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
